import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Typography, Button, TextField, IconButton,
  Alert, CircularProgress, Select, MenuItem, FormControl,
  Tooltip
} from '@mui/material';
import { Add, Delete, ArrowBack, BarChart } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import api from '../api/authApi';

// ─── Grid math (same as RateChart.jsx) ───────────────
function calcInc(val, steps) {
  let inc = 0;
  const sorted = [...steps].sort((a, b) => a.value - b.value);
  for (let i = 0; i < sorted.length - 1; i++) {
    const from = sorted[i].value, to = sorted[i + 1].value, amt = sorted[i].stepAmount;
    if (val <= from) break;
    const count = Math.min(val, to) - from;
    inc += count * amt;
  }
  return Math.round(inc * 10000) / 10000;
}
function genRange(steps) {
  if (!steps || steps.length < 2) return [];
  const sorted = [...steps].sort((a, b) => a.value - b.value);
  const range = [];
  for (let v = sorted[0].value; v <= sorted[sorted.length - 1].value + 0.00001;
       v = Math.round((v + 0.1) * 10) / 10) {
    range.push(Math.round(v * 10) / 10);
  }
  return range;
}
function computeGrid(fatSteps, snfSteps, startingAmount) {
  try {
    const fat = fatSteps.map(s => ({ value: parseFloat(s.value), stepAmount: parseFloat(s.stepAmount) }))
                        .sort((a, b) => a.value - b.value);
    const snf = snfSteps.map(s => ({ value: parseFloat(s.value), stepAmount: parseFloat(s.stepAmount) }))
                        .sort((a, b) => a.value - b.value);
    if (fat.length < 2 || snf.length < 2) return null;
    const fatVals = genRange(fat);
    const snfVals = genRange(snf);
    const base    = parseFloat(startingAmount || 0);
    const grid    = {};
    for (const fv of fatVals) {
      grid[fv] = {};
      const fi = calcInc(fv, fat);
      for (const sv of snfVals) {
        grid[fv][sv] = parseFloat((base + fi + calcInc(sv, snf)).toFixed(2));
      }
    }
    return { fatVals, snfVals, grid };
  } catch { return null; }
}

const EMPTY_FORM = {
  chartType:      'FAT_SNF',
  startingAmount: '',
  adjustmentValue: '0',
  fatSteps:       [{ value: '', stepAmount: '' }],
  snfSteps:       [{ value: '', stepAmount: '' }],
};

export default function MilkSaleRateChart() {
  const { theme } = useApp();
  const [milkTypeId, setMilkTypeId] = useState(1); // 1=Cow, 2=Buffalo
  const [chart,      setChart]      = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [editing,    setEditing]    = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  const fetchChart = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/milk-sale-rate-chart/${milkTypeId}`);
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        const fatSteps = d.fatStepsJson ? JSON.parse(d.fatStepsJson) : [];
        const snfSteps = d.snfStepsJson ? JSON.parse(d.snfStepsJson) : [];
        setChart({ ...d, fatSteps, snfSteps });
        setForm({
          chartType:       d.chartType      || 'FAT_SNF',
          startingAmount:  d.startingAmount || '',
          adjustmentValue: d.adjustmentValue|| '0',
          fatSteps: fatSteps.map(s => ({ value: s.fatValue ?? s.value ?? '', stepAmount: s.stepAmount ?? '' })),
          snfSteps: snfSteps.map(s => ({ value: s.snfValue ?? s.value ?? '', stepAmount: s.stepAmount ?? '' })),
        });
      } else {
        setChart(null);
        setForm(EMPTY_FORM);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [milkTypeId]);

  useEffect(() => { fetchChart(); }, [fetchChart]);

  const handleSave = async () => {
    if (!form.startingAmount)         { setError('Enter starting amount.'); return; }
    if (form.fatSteps.length < 2)     { setError('Add at least 2 FAT steps.'); return; }
    if (form.snfSteps.length < 2)     { setError('Add at least 2 SNF steps.'); return; }
    setSaving(true);
    try {
      const fatSteps = form.fatSteps.map(s => ({ value: parseFloat(s.value), stepAmount: parseFloat(s.stepAmount) }));
      const snfSteps = form.snfSteps.map(s => ({ value: parseFloat(s.value), stepAmount: parseFloat(s.stepAmount) }));
      const gridData = computeGrid(fatSteps, snfSteps, form.startingAmount);
      const gridJson = gridData ? JSON.stringify({
        colHeaders: gridData.snfVals,
        rows: Object.fromEntries(gridData.fatVals.map(fv => [fv, gridData.snfVals.map(sv => gridData.grid[fv][sv])]))
      }) : '{}';
      const res = await api.post('/milk-sale-rate-chart', {
        milkTypeId,
        chartType:       form.chartType,
        startingAmount:  parseFloat(form.startingAmount),
        adjustmentValue: parseFloat(form.adjustmentValue) || 0,
        fatStepsJson:    JSON.stringify(fatSteps),
        snfStepsJson:    JSON.stringify(snfSteps),
        gridJson,
      });
      if (res.data?.success) {
        setSuccess('Rate chart saved!');
        setEditing(false);
        fetchChart();
        setTimeout(() => setSuccess(''), 3000);
      } else setError(res.data?.message || 'Save failed.');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addStep = (type) => {
    const key = type === 'fat' ? 'fatSteps' : 'snfSteps';
    setForm(f => ({ ...f, [key]: [...f[key], { value: '', stepAmount: '' }] }));
  };
  const removeStep = (type, idx) => {
    const key = type === 'fat' ? 'fatSteps' : 'snfSteps';
    setForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));
  };
  const updateStep = (type, idx, field, val) => {
    const key = type === 'fat' ? 'fatSteps' : 'snfSteps';
    setForm(f => ({ ...f, [key]: f[key].map((s, i) => i === idx ? { ...s, [field]: val } : s) }));
  };

  // Grid to display
  const displayChart = editing ? {
    fatSteps: form.fatSteps.map(s => ({ value: parseFloat(s.value), stepAmount: parseFloat(s.stepAmount) })),
    snfSteps: form.snfSteps.map(s => ({ value: parseFloat(s.value), stepAmount: parseFloat(s.stepAmount) })),
    startingAmount: form.startingAmount,
  } : chart;

  const gridData = displayChart && displayChart.fatSteps?.length >= 2 && displayChart.snfSteps?.length >= 2
    ? computeGrid(displayChart.fatSteps, displayChart.snfSteps, displayChart.startingAmount)
    : null;

  const MilkBtn = ({ val, label, emoji }) => (
    <Button
      variant={milkTypeId === val ? 'contained' : 'outlined'}
      size="small"
      onClick={() => { setMilkTypeId(val); setEditing(false); }}
      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', gap: 0.5,
        ...(milkTypeId === val
          ? { background: `linear-gradient(135deg,${theme.primary},${theme.hover})` }
          : { borderColor: theme.border, color: '#374151' }) }}>
      {emoji} {label}
    </Button>
  );

  const thS = { fontSize: '0.75rem', fontWeight: 700, padding: '6px 10px',
    background: '#f3f4f6', border: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'center' };
  const tdS = (highlight) => ({
    fontSize: '0.75rem', padding: '4px 10px', border: '1px solid #e5e7eb',
    textAlign: 'center', whiteSpace: 'nowrap',
    background: highlight ? '#dbeafe' : 'transparent',
  });

  return (
    <Layout title="Milk Sale Rate Chart">
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <BarChart sx={{ color: theme.primary }}/>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Milk Sale Ratechart</Typography>
      </Box>

      {/* Milk type + Change button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>Milk:</Typography>
        <MilkBtn val={1} label="Cow"     emoji="🐄"/>
        <MilkBtn val={2} label="Buffalo" emoji="🐃"/>
        {!editing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
              {milkTypeId === 1 ? 'गाय' : 'म्हैस'} Rate Chart
            </Typography>
            <Button variant="outlined" size="small" onClick={() => setEditing(true)}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.78rem',
                borderColor: theme.primary, color: theme.primary }}>
              Change
            </Button>
          </Box>
        )}
      </Box>

      {loading && <CircularProgress size={24} sx={{ color: theme.primary, display: 'block', mx: 'auto', my: 4 }}/>}

      {!loading && (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* Edit Form */}
          {editing && (
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', p: 2.5, minWidth: 340, maxWidth: 420 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton size="small" onClick={() => setEditing(false)} sx={{ color: theme.primary }}>
                  <ArrowBack sx={{ fontSize: 18 }}/>
                </IconButton>
                <Typography sx={{ fontWeight: 700 }}>Edit Rate Chart</Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1.5, alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Category :</Typography>
                <Typography sx={{ fontSize: '0.82rem' }}>Milk Sale</Typography>

                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Milk Type:</Typography>
                <Typography sx={{ fontSize: '0.82rem' }}>{milkTypeId === 1 ? 'Cow' : 'Buffalo'}</Typography>

                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Type:</Typography>
                <FormControl size="small">
                  <Select value={form.chartType} onChange={e => setForm(f => ({ ...f, chartType: e.target.value }))}
                    sx={{ borderRadius: 1.5, fontSize: '0.82rem' }}>
                    <MenuItem value="FAT_SNF">Increase per fat/snf points</MenuItem>
                    <MenuItem value="FIXED">Fixed Rate</MenuItem>
                  </Select>
                </FormControl>

                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Starting<br/>amount:</Typography>
                <TextField size="small" type="number" value={form.startingAmount}
                  onChange={e => setForm(f => ({ ...f, startingAmount: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }}/>
              </Box>

              {/* FAT & SNF Steps */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {['fat', 'snf'].map(type => {
                  const steps = type === 'fat' ? form.fatSteps : form.snfSteps;
                  const label = type === 'fat' ? 'FAT steps' : 'SNF steps';
                  return (
                    <Box key={type}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{label}</Typography>
                        <Button size="small" startIcon={<Add sx={{ fontSize: 14 }}/>}
                          onClick={() => addStep(type)}
                          sx={{ fontSize: '0.72rem', textTransform: 'none', color: theme.primary, minWidth: 0 }}>
                          + Add
                        </Button>
                      </Box>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...thS, width: '50%' }}>{type.toUpperCase()}</th>
                            <th style={{ ...thS, width: '40%' }}>Inc</th>
                            <th style={{ ...thS, width: '10%' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {steps.map((s, i) => (
                            <tr key={i}>
                              <td style={{ border: '1px solid #e5e7eb', padding: '2px' }}>
                                <input type="number" value={s.value}
                                  onChange={e => updateStep(type, i, 'value', e.target.value)}
                                  style={{ width: '100%', border: 'none', padding: '4px', fontSize: '0.78rem', textAlign: 'center' }}/>
                              </td>
                              <td style={{ border: '1px solid #e5e7eb', padding: '2px' }}>
                                <input type="number" value={s.stepAmount}
                                  onChange={e => updateStep(type, i, 'stepAmount', e.target.value)}
                                  style={{ width: '100%', border: 'none', padding: '4px', fontSize: '0.78rem', textAlign: 'center' }}/>
                              </td>
                              <td style={{ border: '1px solid #e5e7eb', textAlign: 'center' }}>
                                {i > 0 && (
                                  <IconButton size="small" onClick={() => removeStep(type, i)}
                                    sx={{ p: 0.2, color: '#dc2626' }}>
                                    <Delete sx={{ fontSize: 13 }}/>
                                  </IconButton>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => { setForm(EMPTY_FORM); }}
                  sx={{ borderRadius: 1.5, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>
                  Reset
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}
                  sx={{ borderRadius: 1.5, textTransform: 'none',
                    background: `linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
                  {saving ? <CircularProgress size={16} sx={{ color: '#fff' }}/> : 'Save'}
                </Button>
              </Box>
            </Card>
          )}

          {/* Grid Display */}
          {gridData ? (
            <Box sx={{ flex: 1, overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...thS, background: '#e8f0fe', minWidth: 60 }}>fat_snf</th>
                    {gridData.snfVals.map(sv => (
                      <th key={sv} style={{ ...thS, background: '#e8f0fe', minWidth: 52 }}>{sv}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gridData.fatVals.map(fv => (
                    <tr key={fv}>
                      <td style={{ ...tdS(false), fontWeight: 700, background: '#f9fafb' }}>{fv}</td>
                      {gridData.snfVals.map(sv => (
                        <td key={sv} style={tdS(false)}>
                          {gridData.grid[fv][sv].toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : !editing && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 200, color: '#9ca3af', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: '0.9rem' }}>No rate chart configured</Typography>
              <Button variant="outlined" onClick={() => setEditing(true)}
                sx={{ borderRadius: 2, textTransform: 'none', borderColor: theme.primary, color: theme.primary }}>
                + Set Up Rate Chart
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Layout>
  );
}